import { DatePipe, DecimalPipe } from "@angular/common";
import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TranslateService } from "@ngx-translate/core";
import _ from "lodash";
import { of } from "rxjs";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { CommandControlBinder } from "~app/common/services/cqs/command-control-binder/command-control-binder";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { EditKundorderCommand } from "~app/features/forsaljning/kundorder/commands/EditKundorderCommand";
import { KundorderService } from "~app/features/forsaljning/kundorder/kundorder.service";
import { IKundorderDetailKundorder } from "../../../kundorder/models/kundorder-detail-model";

@Component({
    selector: "rask-order-header",
    templateUrl: "./order-header.component.html",
    styleUrls: ["./order-header.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        TextmultilineComponent,
        InputComponent,
        TogglePanelComponent,
        SelectorComponent,
        TextareaComponent,
        DecimalPipe,
        DatePipe
    ]
})
export class OrderHeaderComponent implements OnInit {
    form: UntypedFormGroup;
    @Input() orderDetail: IKundorderDetailKundorder;
    @Input() texts: any;

    private readonly commandService = inject(CommandService);
    private readonly formBinder = inject(FormBinderService);
    private readonly kundorderService = inject(KundorderService);
    private readonly translate = inject(TranslateService);

    get koppladeKunder() {
        if (this.orderDetail.kund?.koppladeKunder?.length > 0) {
            return `${this.orderDetail.huvudkodInternnummer} (${this.orderDetail.kund.koppladeKunder.join(", ")})`;
        }
        return this.orderDetail.huvudkodInternnummer;
    }

    avtalAnsvarigSearch = (input: string) => this.kundorderService.searchForAvtalsansvarigTypeahead(input);

    avtalAnsvarigViewFormatter = (item: any) => item?.namn ?? item?.name;

    avtalTypeAheadDisabled = (item: ITypeAheadResult) => !item?.aktiv;

    avtalTypeAheadValueFormatter = (item: ITypeAheadResult) => item;

    bindControlsToCommands() {
        const kundorderBinder = new CommandControlBinder(this.orderDetail.id, this.commandService, EditKundorderCommand); // Alt skjuta in vilket commando detta ska bindas emot.
        kundorderBinder.bindControl(this.form.controls.beskrivning, "beskrivning");
        kundorderBinder.bindControl(this.form.controls.avtalsansvarig, (v, c) => c.avtalansvarigId = v?.id);
    }

    getPeriodFrom(): Date {
        const dateFrom = this.kundorderService.getPeriodFrom(this.orderDetail.kundorderrader);
        return _.isFinite(dateFrom) ? dateFrom : null;
    }

    getPeriodTom(): Date {
        const dateTom = this.kundorderService.getPeriodTom(this.orderDetail.kundorderrader);
        return _.isFinite(dateTom) ? dateTom : null;
    }

    getSummaLeveransrader(): number {
        return this.kundorderService.getSummaLeveransrader(this.orderDetail.kundorderrader);
    }

    ngOnInit(): void {
        this.setupForm();
        this.bindControlsToCommands();
        this.setupTexts();
    }

    // Om vi vill göra en ändring som är specifik på kund eller terminalorder kan vi lägga dessa en nivå upp och injecta istället. dvs på tex ko-header.
    setAvtalsAnsvarig = (val) => of([val]);

    setupForm(): void {
        this.form = this.formBinder.CreateGroup(this.orderDetail);
    }

    setupTexts() {
        if (this.texts !== undefined) {
            return;
        }
        this.texts = {
            huvudkod: this.translate.instant("KUNDORDER_DETAIL_HUVUDKOD_INTERNNUMMER"),
            adress: this.translate.instant("KUNDORDER_DETAIL_BESOKSADRESS"),
            ordernummer: this.translate.instant("KUNDORDER_DETAIL_ORDERNUMMER"),
            sumLevrader: this.translate.instant("KUNDORDER_DETAIL_SUMMA_LEVERANSRADER"),
            mattslag: this.translate.instant("KUNDORDER_DETAIL_MATTSLAG"),
            from: this.translate.instant("KUNDORDER_DETAIL_FROM"),
            tom: this.translate.instant("KUNDORDER_DETAIL_TOM"),
            ansvarig: this.translate.instant("KUNDORDER_DETAIL_AVTALSANSVARIG"),
            typeaheadText: this.translate.instant("DEFAULT_TYPE_AHEAD_HELP_TEXT"),
            detailBeskrv: this.translate.instant("KUNDORDER_DETAIL_BESKRIVNING"),
            updatedBy: this.translate.instant("KUNDORDER_DETAIL_UBY"),
            createdBy: this.translate.instant("KUNDORDER_DETAIL_IBY")
        };
    }
}
